import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, getDoc, doc, updateDoc, addDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { getAuth, signOut, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';

interface Distributor {
    id: string;
    name?: string;
    email?: string;
    mobile?: string;
    walletBalance?: number;
    role?: string;
}

interface RechargeTransaction {
    id: string;
    distributorId: string;
    distributorName: string;
    adminId: string;
    adminName: string;
    amount: number;
    previousBalance: number;
    newBalance: number;
    timestamp: any;
    description: string;
}

const DistributorRecharge: React.FC = () => {
    const [currentAdmin, setCurrentAdmin] = useState<any>(null);
    const [distributors, setDistributors] = useState<Distributor[]>([]);
    const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
    const [rechargeAmount, setRechargeAmount] = useState('');
    const [loading, setLoading] = useState(true);
    const [recharging, setRecharging] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState<'success' | 'error'>('success');
    const [recentTransactions, setRecentTransactions] = useState<RechargeTransaction[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    const handleLogout = async () => {
        const auth = getAuth();
        await signOut(auth);
        navigate('/signin');
    };

    // Fetch current admin and distributors
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
            if (user) {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setCurrentAdmin({ id: user.uid, ...userData });

                    // Fetch distributors
                    fetchDistributors();
                    // Fetch recent transactions
                    fetchRecentTransactions();
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchDistributors = async () => {
        try {
            const q = query(collection(db, 'users'), where('role', '==', 'distributor'));
            const querySnapshot = await getDocs(q);
            const distributorsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Distributor[];
            setDistributors(distributorsData);
        } catch (error) {
            console.error('Error fetching distributors:', error);
            setMessage('Error fetching distributors');
            setMessageType('error');
        }
    };

    const fetchRecentTransactions = async () => {
        try {
            const q = query(collection(db, 'rechargeTransactions'));
            const querySnapshot = await getDocs(q);
            const transactions = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as RechargeTransaction[];

            // Sort by timestamp (most recent first)
            transactions.sort((a, b) => {
                if (a.timestamp && b.timestamp) {
                    return b.timestamp.toDate() - a.timestamp.toDate();
                }
                return 0;
            });

            setRecentTransactions(transactions.slice(0, 10)); // Show last 10 transactions
        } catch (error) {
            console.error('Error fetching transactions:', error);
        }
    };

    const handleDistributorSelect = (distributor: Distributor) => {
        setSelectedDistributor(distributor);
        setRechargeAmount('');
    };

    const handleRecharge = async () => {
        if (!selectedDistributor || !rechargeAmount || parseFloat(rechargeAmount) <= 0) {
            setMessage('Please enter a valid amount');
            setMessageType('error');
            return;
        }

        const amount = parseFloat(rechargeAmount);
        const previousBalance = selectedDistributor.walletBalance || 0;
        const newBalance = previousBalance + amount;

        setRecharging(true);
        try {
            // Update distributor's wallet balance
            await updateDoc(doc(db, 'users', selectedDistributor.id), {
                walletBalance: newBalance
            });

            // Record recharge transaction
            await addDoc(collection(db, 'rechargeTransactions'), {
                distributorId: selectedDistributor.id,
                distributorName: selectedDistributor.name,
                adminId: currentAdmin.id,
                adminName: currentAdmin.name || 'Admin',
                amount: amount,
                previousBalance: previousBalance,
                newBalance: newBalance,
                timestamp: serverTimestamp(),
                description: `Wallet recharge by admin`
            });

            // Update local state
            setRechargeAmount('');
            setSelectedDistributor(null);
            setMessage(`Successfully recharged ₹${amount} to ${selectedDistributor.name}`);
            setMessageType('success');

            // Refresh data
            fetchDistributors();
            fetchRecentTransactions();

        } catch (error) {
            console.error('Error recharging wallet:', error);
            setMessage('Error recharging wallet. Please try again.');
            setMessageType('error');
        }
        setRecharging(false);
    };

    // Filter distributors based on search query
    const filteredDistributors = distributors.filter(distributor =>
        (distributor.name && distributor.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (distributor.email && distributor.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (distributor.mobile && distributor.mobile.includes(searchQuery))
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-cyan-400 text-xl font-bold animate-pulse">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            {/* Header */}
            <header className="bg-cyan-600 py-4">
                <div className="container mx-auto px-4 flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <img src="/images/logo.png" alt="Fixisy Logo" className="h-8 w-8 object-contain" />
                        Distributor Wallet Recharge
                    </h1>
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Back to Dashboard
                        </button>
                        <button
                            onClick={handleLogout}
                            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 py-6">
                {/* Message Display */}
                {message && (
                    <div className={`mb-4 p-4 rounded-lg ${messageType === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {message}
                    </div>
                )}

                {/* Distributor Search Section */}
                <div className="bg-gray-800 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Select Distributor</h3>
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search by name, email, or mobile"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                        />
                    </div>

                    {/* Distributor List */}
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {filteredDistributors.map((distributor) => (
                            <div
                                key={distributor.id}
                                className={`p-4 rounded-lg cursor-pointer transition-colors ${selectedDistributor?.id === distributor.id
                                        ? 'bg-cyan-600 text-white'
                                        : 'bg-gray-700 text-white hover:bg-gray-600'
                                    }`}
                                onClick={() => handleDistributorSelect(distributor)}
                            >
                                <div className="font-semibold">{distributor.name}</div>
                                <div className="text-sm opacity-80">
                                    {distributor.email} • {distributor.mobile}
                                </div>
                                <div className="text-sm opacity-80">
                                    Current Balance: ₹{distributor.walletBalance || 0}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recharge Section */}
                {selectedDistributor && (
                    <div className="bg-gray-800 rounded-lg p-6 mb-6">
                        <h3 className="text-xl font-semibold text-white mb-4">Recharge Wallet</h3>
                        <div className="bg-gray-700 rounded-lg p-4 mb-4">
                            <div className="text-white font-semibold">
                                {selectedDistributor.name}
                            </div>
                            <div className="text-gray-300 text-sm">
                                {selectedDistributor.email} • {selectedDistributor.mobile}
                            </div>
                            <div className="text-cyan-400 text-sm">
                                Current Balance: ₹{selectedDistributor.walletBalance || 0}
                            </div>
                        </div>
                        <div className="flex gap-2 mb-4">
                            <input
                                type="number"
                                placeholder="Enter recharge amount"
                                value={rechargeAmount}
                                onChange={(e) => setRechargeAmount(e.target.value)}
                                className="flex-1 px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                min="1"
                            />
                            <button
                                onClick={handleRecharge}
                                disabled={recharging || !rechargeAmount || parseFloat(rechargeAmount) <= 0}
                                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                            >
                                {recharging ? 'Recharging...' : 'Recharge'}
                            </button>
                        </div>
                        <button
                            onClick={() => setSelectedDistributor(null)}
                            className="text-gray-400 hover:text-white"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {/* Recent Recharge Transactions */}
                <div className="bg-gray-800 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Recent Recharge Transactions</h3>
                    {recentTransactions.length > 0 ? (
                        <div className="space-y-3">
                            {recentTransactions.map((transaction) => (
                                <div key={transaction.id} className="bg-gray-700 rounded-lg p-4">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="text-white font-semibold">
                                                {transaction.distributorName}
                                            </div>
                                            <div className="text-gray-300 text-sm">
                                                Recharged by {transaction.adminName}
                                            </div>
                                            <div className="text-gray-400 text-xs">
                                                {transaction.timestamp?.toDate?.()?.toLocaleString() || 'N/A'}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-green-400 font-bold text-lg">
                                                +₹{transaction.amount}
                                            </div>
                                            <div className="text-gray-400 text-sm">
                                                Balance: ₹{transaction.newBalance}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-gray-400 text-center py-8">
                            No recharge transactions yet
                        </div>
                    )}
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-800 py-4 text-center text-gray-400 text-sm">
                &copy; {new Date().getFullYear()} Kifayti Retail Private Limited. All rights reserved.
            </footer>
        </div>
    );
};

export default DistributorRecharge;
